/**
 * Partial Delta Sync Engine
 * 
 * Based on RFC: partial-delta-sync.md
 * 
 * Implements recursive depth-based sync between in-memory source of truth and remote server.
 * 
 * Key concepts (avoiding previous confusion):
 * 1. LEVEL/DEPTH SYNC: Recursive sync that processes objects level by level (depth 0, 1, 2, etc.)
 * 2. SUBTREE FETCH: Lazy loading of missing subtree content for a specific object
 * 
 * Responsibilities:
 * - Recursive bi-directional sync starting from root object
 * - Compare hashes and version vectors level by level
 * - Push/pull only differences
 * - Handle lazy loading of missing subtrees when needed
 */

import { InMemorySourceOfTruth, ObjectMetadata } from './InMemorySourceOfTruth';

export interface SyncResult {
  pushedCount: number;
  pulledCount: number;
}

export interface SyncOptions {
  maxDepth?: number;
  fetchUnknownSubtrees?: boolean; // true for offline mode
  serverUrl?: string;
  documentId?: string;
  isStructuralSync?: boolean; // true for document loading, false for content changes
}

export interface RemoteObjectData {
  objectId: string;
  contentHash: string;
  subtreeHash: string;
  versionVector: Record<string, number>;
  depth: number;
  parentId?: string;
}

export interface RemoteProperty {
  objectId: string;
  propertyName: string;
  value: any;
  replicaId: number;
  logicalTimestamp: number;
}

export class PartialDeltaSyncEngine {
  private sourceOfTruth: InMemorySourceOfTruth;
  private serverUrl: string;
  private documentId: string;
  private syncInProgress: boolean = false;
  private onStructuralSyncingChange?: (isStructuralSyncing: boolean) => void;

  constructor(
    sourceOfTruth: InMemorySourceOfTruth,
    serverUrl: string,
    documentId: string,
    onStructuralSyncingChange?: (isStructuralSyncing: boolean) => void
  ) {
    this.sourceOfTruth = sourceOfTruth;
    this.serverUrl = serverUrl;
    this.documentId = documentId;
    this.onStructuralSyncingChange = onStructuralSyncingChange;
  }

  /**
   * MAIN SYNC METHOD: Recursive bi-directional sync
   * Implements RFC section: "Recursive sync"
   */
  async sync(options: SyncOptions = {}): Promise<SyncResult> {
    if (this.syncInProgress) {
      throw new Error('Sync already in progress');
    }

    this.syncInProgress = true;
    const maxDepth = options.maxDepth || 10;
    const fetchUnknownSubtrees = options.fetchUnknownSubtrees || false;


    try {
      let totalResult: SyncResult = { pushedCount: 0, pulledCount: 0 };

      console.log(`🔄 Starting sync for document ${this.documentId}`);

      // Ensure all hashes are computed before sync
      this.sourceOfTruth.recomputeHashes();

      // Collect unknown subtree parent IDs across all depths
      const unknownSubtreeParentIds = new Set<string>();

      // Recursive sync level by level
      for (let depth = 0; depth <= maxDepth; depth++) {
        
        const levelResult = await this.syncDepthLevel(depth, fetchUnknownSubtrees, unknownSubtreeParentIds);
        
        totalResult.pushedCount += levelResult.pushedCount;
        totalResult.pulledCount += levelResult.pulledCount;

        // Continue checking all depths - don't break early
        // Changes might exist at deeper levels even if parent levels are unchanged
      }

      // Always log completion, with different messages based on whether changes occurred
      if (totalResult.pushedCount > 0 || totalResult.pulledCount > 0) {
        console.log(`✅ Sync complete: pushed ${totalResult.pushedCount}, pulled ${totalResult.pulledCount} properties`);
      } else {
        console.log(`✅ Sync complete: no changes needed (all hashes match)`);
      }

      return totalResult;

    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * SUBTREE FETCH: Lazy load missing subtree content for a specific object
   * Implements RFC section: "Loading pages"
   */
  async fetchSubtreeIfNeeded(objectId: string): Promise<SyncResult> {
    const localMetadata = this.sourceOfTruth.getObjectMetadata(objectId);
    
    // If object doesn't exist locally, fetch it
    if (!localMetadata) {
      return await this.fetchSubtreeProperties(objectId);
    }
    
    // If object exists but has no subtree hash computed yet, fetch it
    if (!localMetadata.subtreeHash) {
      return await this.fetchSubtreeProperties(objectId);
    }
    
    // If subtree hash is "0", this means no children exist locally
    // For structural objects (doc/page), we should attempt to fetch children (lazy loading)
    if (localMetadata.subtreeHash === '0') {
      return await this.fetchSubtreeProperties(objectId);
    }
    
    // Object has non-zero subtree hash, meaning it has been processed and has children
    return { pushedCount: 0, pulledCount: 0 };
  }

  /**
   * Check if sync is currently in progress
   */
  isSyncInProgress(): boolean {
    return this.syncInProgress;
  }


  // Private methods for level-by-level sync

  private async syncDepthLevel(depth: number, fetchUnknownSubtrees: boolean, unknownSubtreeParentIds: Set<string>): Promise<SyncResult> {
    // Step 1: Get local objects at this depth
    const localObjects = this.getLocalObjectsAtDepth(depth);
    
    // Step 2: Get remote objects at this depth
    const remoteObjects = await this.fetchRemoteObjectsAtDepth(depth);
    
    // Step 3: Update cumulative set of parent IDs with subtree_hash=0 (unknown subtrees)
    if (!fetchUnknownSubtrees) {
      for (const localObj of localObjects) {
        if (!localObj.subtreeHash || localObj.subtreeHash === '0') {
          unknownSubtreeParentIds.add(localObj.objectId);
        }
      }
    }
    
    // Step 4: Compare and sync differences
    const result = await this.compareAndSync(localObjects, remoteObjects, fetchUnknownSubtrees, unknownSubtreeParentIds);
    
    // Log if this depth had significant activity
    
    return result;
  }

  private getLocalObjectsAtDepth(depth: number): ObjectMetadata[] {
    const allObjects = this.sourceOfTruth.getAllObjects();
    const rootObjects = allObjects.filter(obj => obj.parentId === null);
    
    if (depth === 0) {
      return rootObjects;
    }

    // For depth > 0, find objects at that specific depth
    const objectsAtDepth: ObjectMetadata[] = [];
    
    for (const rootObj of rootObjects) {
      this.collectObjectsAtDepth(rootObj.objectId, depth, 0, objectsAtDepth);
    }
    
    return objectsAtDepth;
  }

  private collectObjectsAtDepth(
    objectId: string, 
    targetDepth: number, 
    currentDepth: number, 
    result: ObjectMetadata[]
  ): void {
    if (currentDepth === targetDepth) {
      const metadata = this.sourceOfTruth.getObjectMetadata(objectId);
      if (metadata) {
        result.push(metadata);
      }
      return;
    }

    if (currentDepth < targetDepth) {
      const children = this.sourceOfTruth.getChildObjects(objectId);
      for (const childId of children) {
        this.collectObjectsAtDepth(childId, targetDepth, currentDepth + 1, result);
      }
    }
  }

  /**
   * Lightweight check if document exists on remote server
   * Uses /exists endpoint to avoid expensive hash computation
   */
  async checkDocumentExists(): Promise<boolean> {
    try {
      const url = `${this.serverUrl}/api/documents/${this.documentId}/exists`;
      const response = await fetch(url);
      
      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.exists === true;
    } catch (error) {
      console.warn('Failed to check document existence:', error);
      return false;
    }
  }

  /**
   * Delete document from remote server
   * This belongs in sync engine since it handles server communication
   */
  async deleteDocumentFromRemote(): Promise<boolean> {
    try {
      const response = await fetch(`${this.serverUrl}/api/documents/${this.documentId}/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return true;
      } else if (response.status === 404) {
        // Document doesn't exist on server, which is fine for deletion
        return true;
      } else {
        console.error(`Failed to delete document from server: ${response.status} ${response.statusText}`);
        return false;
      }
    } catch (error) {
      console.error('Network error while deleting document from server:', error);
      return false;
    }
  }

  private async fetchRemoteObjectsAtDepth(depth: number): Promise<RemoteObjectData[]> {
    try {
      const url = `${this.serverUrl}/api/documents/${this.documentId}/hashes?depth=${depth}&include_version_vectors=true`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch remote objects at depth ${depth}: ${response.status}`);
      }

      const data = await response.json();
      const remoteObjects: RemoteObjectData[] = [];

      if (data.object_hashes && Array.isArray(data.object_hashes)) {
        for (const obj of data.object_hashes) {
          remoteObjects.push({
            objectId: obj.object_id,
            contentHash: obj.content_hash,
            subtreeHash: obj.subtree_hash,
            versionVector: obj.version_vector || {},
            depth: depth,
            parentId: obj.parent_id
          });
        }
      }

      return remoteObjects;

    } catch (error) {
      console.error(`❌ [PARTIAL DELTA SYNC] Failed to fetch remote objects at depth ${depth}:`, error);
      throw error;
    }
  }

  private async compareAndSync(
    localObjects: ObjectMetadata[],
    remoteObjects: RemoteObjectData[],
    fetchUnknownSubtrees: boolean,
    unknownSubtreeParentIds: Set<string>
  ): Promise<SyncResult> {
    let result: SyncResult = { pushedCount: 0, pulledCount: 0 };

    // Create maps for efficient lookup
    const localMap = new Map<string, ObjectMetadata>();
    const remoteMap = new Map<string, RemoteObjectData>();

    for (const local of localObjects) {
      localMap.set(local.objectId, local);
    }

    for (const remote of remoteObjects) {
      remoteMap.set(remote.objectId, remote);
    }

    // Process all unique object IDs
    const allObjectIds = new Set([...Array.from(localMap.keys()), ...Array.from(remoteMap.keys())]);

    for (const objectId of Array.from(allObjectIds)) {
      const local = localMap.get(objectId);
      const remote = remoteMap.get(objectId);

      // CRITICAL: Skip objects whose parent is an unknown subtree
      if (!fetchUnknownSubtrees && remote?.parentId && unknownSubtreeParentIds.has(remote.parentId)) {
        continue;
      }

      if (local && remote) {
        // Object exists both locally and remotely - compare hashes
        const syncResult = await this.syncObjectDifferences(local, remote);
        result.pushedCount += syncResult.pushedCount;
        result.pulledCount += syncResult.pulledCount;

      } else if (local && !remote) {
        // Object exists locally but not remotely - push to remote (no filtering needed)
        const pushResult = await this.pushObjectToRemote(local);
        result.pushedCount += pushResult.pushedCount;

      } else if (!local && remote) {
        // Object exists remotely but not locally
        if (fetchUnknownSubtrees || remote.subtreeHash !== '0') {
          // Pull from remote if we're in offline mode OR if it has content (no filtering needed)
          const pullResult = await this.pullObjectFromRemote(remote);
          result.pulledCount += pullResult.pulledCount;
        } else {
        }
      }
    }

    return result;
  }

  private async syncObjectDifferences(local: ObjectMetadata, remote: RemoteObjectData): Promise<SyncResult> {
    let result: SyncResult = { pushedCount: 0, pulledCount: 0 };

    // First compare content hashes (cheap comparison)
    if (local.contentHash === remote.contentHash) {
      // Content is identical, no need to sync properties
      return result;
    }

    
    // Debug: Show what properties each side has
    const localProps = this.sourceOfTruth.getObjectProperties(local.objectId);
    
    if (remote.versionVector) {
    }
    
    // Content differs, now use version vectors to determine sync direction
    const comparison = this.compareVersionVectors(local.versionVector || {}, remote.versionVector);
    
    if (comparison === 'local_newer') {
      const pushResult = await this.pushObjectToRemote(local, remote);
      result.pushedCount += pushResult.pushedCount;
    } else if (comparison === 'remote_newer') {
      const pullResult = await this.pullObjectFromRemote(remote, local);
      result.pulledCount += pullResult.pulledCount;
    } else if (comparison === 'concurrent') {
      // Concurrent updates - pull remote first to let LWW resolution work, then push local changes
      const pullResult = await this.pullObjectFromRemote(remote, local);
      result.pulledCount += pullResult.pulledCount;
      const pushResult = await this.pushObjectToRemote(local, remote);
      result.pushedCount += pushResult.pushedCount;
    } else {
      // comparison === 'same' - version vectors identical but content differs
      // This can happen if server didn't update version vectors properly
      const pullResult = await this.pullObjectFromRemote(remote, local);
      result.pulledCount += pullResult.pulledCount;
      const pushResult = await this.pushObjectToRemote(local, remote);
      result.pushedCount += pushResult.pushedCount;
    }

    return result;
  }

  private compareVersionVectors(
    local: Record<string, number>, 
    remote: Record<string, number>
  ): 'local_newer' | 'remote_newer' | 'concurrent' | 'same' {
    let localNewer = false;
    let remoteNewer = false;

    // Get all replica IDs from both vectors
    const allReplicas = new Set([...Object.keys(local), ...Object.keys(remote)]);

    for (const replicaId of Array.from(allReplicas)) {
      const localTimestamp = local[replicaId] || 0;
      const remoteTimestamp = remote[replicaId] || 0;

      if (localTimestamp > remoteTimestamp) {
        localNewer = true;
      } else if (remoteTimestamp > localTimestamp) {
        remoteNewer = true;
      }
    }

    if (localNewer && !remoteNewer) return 'local_newer';
    if (remoteNewer && !localNewer) return 'remote_newer';
    if (!localNewer && !remoteNewer) return 'same';
    return 'concurrent';
  }

  private async pushObjectToRemote(local: ObjectMetadata, remote?: RemoteObjectData): Promise<SyncResult> {
    
    // Get all properties for this object
    const allProperties = this.sourceOfTruth.getObjectProperties(local.objectId);
    
    // Filter properties to push based on version vectors (property-level delta sync)
    let propertiesToPush = allProperties;
    if (remote?.versionVector) {
      propertiesToPush = allProperties.filter(prop => {
        const remoteTimestamp = remote.versionVector[prop.replicaId] || 0;
        const shouldPush = prop.logicalTimestamp > remoteTimestamp;
        if (shouldPush) {
        }
        return shouldPush;
      });
    }
    
    let pushedCount = 0;

    try {
      // Use the sync endpoint for batch processing
      if (propertiesToPush.length > 0) {
        const changes = propertiesToPush.map(prop => ({
          object_id: prop.objectId,
          property: prop.propertyName,
          value: typeof prop.value === 'object' && prop.value !== null 
            ? JSON.stringify(prop.value)  // Convert objects to JSON strings for server
            : prop.value,                 // Keep primitives as-is
          lww_counter: prop.lwwCounter, // Keep null for special properties
          lww_random: prop.lwwRandom,   // Keep null for special properties
          replica_id: prop.replicaId,
          logical_timestamp: prop.logicalTimestamp,
          updated_at: Date.now(),
          user_id: 'system' // Required by server
        }));

        const response = await fetch(`${this.serverUrl}/api/documents/${this.documentId}/sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            client_state: {
              last_sync_timestamp: 0
            },
            changes
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to push ${propertiesToPush.length} properties for object ${local.objectId}: ${response.status}`);
        }

        pushedCount = propertiesToPush.length;
      }

      return { pushedCount, pulledCount: 0 };

    } catch (error) {
      console.error(`❌ [PARTIAL DELTA SYNC] Failed to push object ${local.objectId}:`, error);
      throw error;
    }
  }

  private async pullObjectFromRemote(remote: RemoteObjectData, local?: ObjectMetadata): Promise<SyncResult> {
    
    try {
      const response = await fetch(`${this.serverUrl}/api/documents/${this.documentId}/properties?object_id=${remote.objectId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to pull object ${remote.objectId}: ${response.status}`);
      }

      const data = await response.json();
      let pulledCount = 0;

      if (data.properties && Array.isArray(data.properties)) {
        // Sort properties with object.metadata first, then by replica_id and logical_timestamp
        const sortedProperties = data.properties.sort((a: any, b: any) => {
          // object.metadata must always come first
          if (a.property === 'object.metadata' && b.property !== 'object.metadata') {
            return -1;
          }
          if (b.property === 'object.metadata' && a.property !== 'object.metadata') {
            return 1;
          }
          
          // Then sort by replica_id and logical_timestamp
          if (a.replica_id !== b.replica_id) {
            return a.replica_id - b.replica_id;
          }
          return a.logical_timestamp - b.logical_timestamp;
        });
        
        for (const prop of sortedProperties) {
          // Filter properties to pull based on version vectors (property-level delta sync)
          let shouldPull = true;
          if (local?.versionVector) {
            const localTimestamp = local.versionVector[prop.replica_id] || 0;
            shouldPull = prop.logical_timestamp > localTimestamp;
          }
          
          if (shouldPull) {
            this.sourceOfTruth.addProperty({
              objectId: prop.object_id,
              propertyName: prop.property,
              value: prop.value,
              lwwCounter: prop.lww_counter,
              lwwRandom: prop.lww_random,
              replicaId: prop.replica_id,
              logicalTimestamp: prop.logical_timestamp,
              source: 'remote',
              addedToMemoryAt: Date.now()
            });
            pulledCount++;
          }
        }
        
      }

      return { pushedCount: 0, pulledCount: pulledCount };

    } catch (error) {
      console.error(`❌ [PARTIAL DELTA SYNC] Failed to pull object ${remote.objectId}:`, error);
      throw error;
    }
  }

  private async fetchSubtreeProperties(objectId: string): Promise<SyncResult> {
    console.log(`📥 Fetching subtree for ${objectId}`);
    
    try {
      const response = await fetch(`${this.serverUrl}/api/documents/${this.documentId}/fetch-subtrees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          root_object_ids: [objectId],
          max_depth: 10
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch subtree for ${objectId}: ${response.status}`);
      }

      const data = await response.json();
      let pulledCount = 0;

      // Process subtrees data format: { subtrees: [{ objects: [...] }] }
      if (data.subtrees && Array.isArray(data.subtrees)) {
        for (const subtree of data.subtrees) {
          if (subtree.objects && Array.isArray(subtree.objects)) {
            for (const obj of subtree.objects) {
              if (obj.properties && Array.isArray(obj.properties)) {
                // Sort properties with object.metadata first, then by replica_id and logical_timestamp
                const sortedProperties = obj.properties.sort((a: any, b: any) => {
                  // object.metadata must always come first
                  if (a.property === 'object.metadata' && b.property !== 'object.metadata') {
                    return -1;
                  }
                  if (b.property === 'object.metadata' && a.property !== 'object.metadata') {
                    return 1;
                  }
                  
                  // Then sort by replica_id and logical_timestamp
                  if (a.replica_id !== b.replica_id) {
                    return a.replica_id - b.replica_id;
                  }
                  return a.logical_timestamp - b.logical_timestamp;
                });
                
                for (const prop of sortedProperties) {
                  this.sourceOfTruth.addProperty({
                    objectId: obj.object_id, // Use the parent object's object_id, not prop.object_id
                    propertyName: prop.property,
                    value: prop.value,
                    lwwCounter: prop.lww_counter,
                    lwwRandom: prop.lww_random,
                    replicaId: prop.replica_id,
                    logicalTimestamp: prop.logical_timestamp,
                    source: 'remote',
                    addedToMemoryAt: Date.now()
                  });
                  pulledCount++;
                }
              }
            }
          }
        }
      }

      if (pulledCount > 0) {
        console.log(`✅ Fetched ${pulledCount} properties for subtree ${objectId}`);
      }

      return { pushedCount: 0, pulledCount: pulledCount };

    } catch (error) {
      console.error(`❌ [PARTIAL DELTA SYNC] Failed to fetch subtree ${objectId}:`, error);
      throw error;
    }
  }
}
